// The questions the task's running step waits on (D64): a single question as the run page's
// answer card, a batch as one card holding every item still pending with its progress. Each item
// is answered through the usual per-question path; the run resumes once the last is in.
import { Stack } from "@phosphor-icons/react";

import type { Question } from "../../api/client";
import type { ConversationState } from "../../conversation/model";
import { findQuestion } from "../../conversation/question";
import { QuestionCard, type QuestionAccess } from "../../pages/QuestionCard";
import { cardClass } from "../../ui/Card";
import { cn } from "../../ui/cn";
import { Pill } from "../../ui/status";
import { batchItem, batchProgress, groupPending } from "../batches";
import { useT } from "../../i18n";
import { QUESTIONS_ANCHOR } from "./QuestionsPin";

interface Props {
  questions: Question[];
  state: ConversationState | undefined;
  access: QuestionAccess;
}

export function PendingQuestions({ questions, state, access }: Props) {
  const t = useT().tasks.task;
  const card = (q: Question, where?: string) => (
    <QuestionCard key={q.id} question={q} asked={state ? findQuestion(state.events, q.id) : undefined} access={access} where={where} />
  );
  return (
    <div id={QUESTIONS_ANCHOR} className="scroll-mt-24 space-y-4" data-testid="pending-questions">
      {groupPending(questions).map((g) =>
        g.kind === "single" ? (
          card(g.question)
        ) : (
          <section key={g.batchId} className={cn(cardClass, "space-y-3 p-4")} aria-label={t.batchLabel} data-testid="question-batch">
            <div className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-gray-900">
              <Stack weight="duotone" className="h-5 w-5 text-orange" aria-hidden />
              {t.batchAsked(g.size)}
              <Pill tone="orange">{batchProgress(g)}</Pill>
            </div>
            <p className="m-0 text-[12.5px] text-gray-500">{t.batchHelp}</p>
            {g.pending.map((q) => {
              const item = batchItem(q);
              return card(q, item ? t.batchItem(item) : undefined);
            })}
          </section>
        ),
      )}
    </div>
  );
}
